import { Controller } from "@hotwired/stimulus"

// Validates field mappings against the actual Salesforce API
export default class extends Controller {
  static values = {
    url: String
  }

  async validate(event) {
    event.preventDefault()

    const button = event.currentTarget
    const originalContent = button.innerHTML

    // Show loading state
    button.disabled = true
    button.innerHTML = `
      <div class="flex-shrink-0">
        <svg class="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <div class="ml-3">
        <p class="text-sm font-medium text-white">Validating...</p>
        <p class="text-sm text-gray-400">Connecting to Salesforce</p>
      </div>
    `

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      const data = await response.json()

      // Update the results frame
      this.displayResults(data)

    } catch (error) {
      console.error('Salesforce validation error:', error)
      this.displayResults({
        success: false,
        error: 'Failed to connect to Salesforce. Please try again.',
        error_type: 'network'
      })
    } finally {
      // Restore button
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  displayResults(data) {
    const resultsContainer = document.querySelector('#salesforce_validation_results')

    if (!resultsContainer) {
      // Create the container if it doesn't exist
      const container = document.createElement('turbo-frame')
      container.id = 'salesforce_validation_results'
      const validationSection = document.querySelector('[data-controller="salesforce-validate"]')?.closest('.mb-6')?.previousElementSibling
      if (validationSection) {
        validationSection.insertAdjacentElement('afterend', container)
      }
    }

    const frame = document.querySelector('#salesforce_validation_results') || document.querySelector('turbo-frame#salesforce_validation_results')

    if (frame) {
      frame.innerHTML = this.buildResultsHtml(data)
    }
  }

  buildResultsHtml(data) {
    if (data.success) {
      const validCount = data.valid_fields?.length || 0
      const invalidCount = data.invalid_fields?.length || 0
      const totalChecked = data.total_checked || 0

      const headerClass = invalidCount === 0 ? 'text-green-400' : 'text-yellow-400'
      const headerIcon = invalidCount === 0
        ? `<svg class="h-6 w-6 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>`
        : `<svg class="h-6 w-6 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>`
      const headerText = invalidCount === 0 ? 'All Fields Valid in Salesforce' : 'Some Fields Missing in Salesforce'

      const validFieldsHtml = data.valid_fields?.map(f => `
        <li class="flex items-center">
          <span class="font-mono text-green-400">${f.salesforce_field}</span>
          <span class="text-gray-500 mx-1">&larr;</span>
          <span class="text-gray-400">${f.application_field}</span>
        </li>
      `).join('') || '<li class="text-gray-500 italic">No valid fields</li>'

      const invalidFieldsHtml = data.invalid_fields?.map(f => `
        <li class="flex items-center">
          <span class="font-mono text-red-400">${f.salesforce_field}</span>
          <span class="text-gray-500 mx-1">&larr;</span>
          <span class="text-gray-400">${f.application_field}</span>
          ${f.field_type === 'custom' ? '<span class="ml-2 px-1 py-0.5 text-xs bg-purple-900/30 text-purple-400 rounded">custom</span>' : ''}
        </li>
      `).join('') || '<li class="text-gray-500 italic">All mapped fields exist in Salesforce!</li>'

      return `
        <div class="bg-admin-dark rounded-lg shadow-sm border border-emerald-900/30 p-4" id="salesforce-validation-message">
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
              ${headerIcon}
              <h3 class="text-lg font-medium ${headerClass}">${headerText}</h3>
            </div>
            <button type="button" onclick="document.getElementById('salesforce-validation-message').remove()" class="text-gray-400 hover:text-gray-300" title="Close">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="text-sm text-gray-400 mb-4">
            Validated ${totalChecked} active field mappings against Salesforce
            <span class="text-gray-500">at ${data.validated_at}</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-green-900/10 border border-green-900/30 rounded-lg p-4">
              <h4 class="text-sm font-medium text-green-400 mb-2 flex items-center">
                <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                Valid Fields (${validCount})
              </h4>
              <ul class="text-xs text-green-300 space-y-1 max-h-48 overflow-y-auto">
                ${validFieldsHtml}
              </ul>
            </div>

            <div class="bg-red-900/10 border border-red-900/30 rounded-lg p-4">
              <h4 class="text-sm font-medium text-red-400 mb-2 flex items-center">
                <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                Missing in Salesforce (${invalidCount})
              </h4>
              <ul class="text-xs text-red-300 space-y-1 max-h-48 overflow-y-auto">
                ${invalidFieldsHtml}
              </ul>
              ${invalidCount > 0 ? '<p class="mt-2 text-xs text-gray-400">These fields need to be created in Salesforce or removed from mappings.</p>' : ''}
            </div>
          </div>
        </div>
      `
    } else {
      // Error state
      return `
        <div class="bg-admin-dark rounded-lg shadow-sm border border-red-900/30 p-4" id="salesforce-validation-message">
          <div class="flex items-start">
            <svg class="h-6 w-6 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <div class="flex-1">
              <h3 class="text-lg font-medium text-red-400">Salesforce Validation Failed</h3>
              <p class="text-sm text-red-300 mt-1">${data.error}</p>
              ${data.error_type === 'authentication' ? '<p class="text-xs text-gray-400 mt-2">Check your Salesforce environment credentials and try again.</p>' : ''}
            </div>
            <button type="button" onclick="document.getElementById('salesforce-validation-message').remove()" class="text-gray-400 hover:text-gray-300 ml-2" title="Close">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      `
    }
  }
}
